import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { BaseAsyncService } from '@core/state/base-service'
import { ApiPaginatedListResponseDto, TrackingResearchDto } from '@shared/models/dto/api-dtos'
import { ApiSingleResponseWithData, ApiSimpleResponse } from '@shared/models/interfaces'

export interface DiFollowupFormValue {
  nbSuccessors: string
  ongoingLocations: string
  completedLocations: string
  difficulties: string
}

export interface ResearcherFollowupFormValue {
  nombreHeritiers: string
  departementEnCours: string
  departementTerminees: string
  difficultes: string
}

export interface ResearcherFollowupRequest extends DiFollowupFormValue {
  successionFileId: string
}

export interface FollowupRequestDto {
  id: number
  successionFileId: string
  type: number
  ongoingLocations: string
  completedLocations: string
  difficulties: string
  nbSuccessors: string
  createdByUserId: string
  createdAt: string
  isRead: boolean
  readAt: string
}

export interface PaginationParams {
  type?: 0 | 1
  page?: number
  pageSize?: number
  phase?: string //'RGL'
}

export interface MessageRequest {
  successionFileId: string
  objetMessage: string
  message: string
  phase?: string
  /** Héritier destinataire (messages héritiers uniquement). Null/absent = tous les héritiers. */
  successorId?: number | null
}

export interface HistoryMessageDto {
  id: number
  objetMessage: string
  message: string
  /** Héritier destinataire. Null = message envoyé à tous les héritiers. */
  successorId?: number | null
  /** Nom de l'héritier destinataire, résolu par l'API (null si broadcast). */
  successorName?: string | null
  createdByUserId: string
  createdAt: string
}

export type followersType = 'followup' | 'notary' | 'heir'

@Injectable({ providedIn: 'root' })
export class TrackingResearchService extends BaseAsyncService {
  private readonly ENDPOINT = '/tracking-research'

  getTrackingResearch(successionFileId: string): Observable<ApiSingleResponseWithData<TrackingResearchDto>> {
    return this.get<ApiSingleResponseWithData<TrackingResearchDto>>(`${this.ENDPOINT}/${successionFileId}`)
  }

  sendNotaryMessage(body: MessageRequest): Observable<ApiSingleResponseWithData<HistoryMessageDto>> {
    return this.post<ApiSingleResponseWithData<HistoryMessageDto>>(`${this.ENDPOINT}/notary-message`, body)
  }

  getNotaryMessages(
    successionFileId: string,
    params: PaginationParams
  ): Observable<ApiPaginatedListResponseDto<HistoryMessageDto>> {
    const queryParams = this.buildParams(params)
    return this.getWithOptions<ApiPaginatedListResponseDto<HistoryMessageDto>>(
      `${this.ENDPOINT}/notary-messages/${successionFileId}`,
      { params: queryParams }
    )
  }

  sendHeirMessage(body: MessageRequest): Observable<ApiSingleResponseWithData<HistoryMessageDto>> {
    return this.post<ApiSingleResponseWithData<HistoryMessageDto>>(`${this.ENDPOINT}/heir-message`, body)
  }

  getHeirMessages(
    successionFileId: string,
    params: PaginationParams
  ): Observable<ApiPaginatedListResponseDto<HistoryMessageDto>> {
    const queryParams = this.buildParams(params)
    return this.getWithOptions<ApiPaginatedListResponseDto<HistoryMessageDto>>(
      `${this.ENDPOINT}/heir-messages/${successionFileId}`,
      { params: queryParams }
    )
  }

  buildParams(params: PaginationParams) {
    const queryParams: Record<string, string> = Object.fromEntries(
      Object.entries(params)
        .filter(([_, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => [key, String(value)])
    )
    return queryParams
  }
  sendResearcherFollowup(body: ResearcherFollowupRequest): Observable<ApiSimpleResponse> {
    return this.post<ApiSimpleResponse>(`${this.ENDPOINT}/researcher-followup`, body)
  }

  sendDiFollowup(body: ResearcherFollowupRequest): Observable<ApiSimpleResponse> {
    return this.post<ApiSimpleResponse>(`${this.ENDPOINT}/di-followup`, body)
  }

  getFollowupRequests(
    successionFileId: string,
    params: PaginationParams = { pageSize: 3 }
  ): Observable<ApiPaginatedListResponseDto<FollowupRequestDto>> {
    const queryParams: Record<string, string> = {}
    if (params.type !== undefined) queryParams['type'] = String(params.type)
    if (params.page !== undefined) queryParams['page'] = String(params.page)
    queryParams['pageSize'] = String(params.pageSize)

    return this.getWithOptions<ApiPaginatedListResponseDto<FollowupRequestDto>>(
      `${this.ENDPOINT}/followup-requests/${successionFileId}`,
      { params: queryParams }
    )
  }

  markFollowupRequestAsRead(id: number): Observable<ApiSimpleResponse> {
    return this.post<ApiSimpleResponse>(`${this.ENDPOINT}/followup-requests/${id}/read`, {})
  }

  sendTrackingRequest(successionFileId: string, type: number): Observable<ApiSimpleResponse> {
    return this.post<ApiSimpleResponse>(`${this.ENDPOINT}/tracking-request/${successionFileId}`, { type })
  }
}
